import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'

interface DrawerProps {
  isOpen: boolean
  onClose: () => void
  title: string
  description?: string
  icon?: React.ReactNode
  children: React.ReactNode
  width?: 'sm' | 'md' | 'lg' | 'xl'
  isCustomWidth?: boolean
  customWidth?: string
}

const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  title,
  description,
  icon,
  children,
  width = 'lg',
  isCustomWidth = false,
  customWidth
}) => {
  const getWidth = () => {
    switch (width) {
      case 'sm':
        return 'max-w-sm'
      case 'md':
        return 'max-w-md'
      case 'lg':
        return 'max-w-lg'
      case 'xl':
        return 'max-w-xl'
      default:
        return 'max-w-lg'
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className='fixed inset-0 bg-black bg-opacity-50 z-40'
          />

          {/* Drawer */}
          <motion.div
            initial={{ opacity: 0, right: '-100%' }}
            animate={{ opacity: 1, right: 0 }}
            exit={{ opacity: 0, right: '-100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className={`fixed top-0 h-full bg-white shadow-2xl z-50 ${
              isCustomWidth ? customWidth : getWidth()
            } w-full overflow-y-auto border-l border-gray-200`}
          >
            {/* Header */}
            <div className='sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center space-x-3'>
                  {icon && (
                    <div className='p-2 bg-blue-100 rounded-lg'>
                      <div className='text-blue-600'>{icon}</div>
                    </div>
                  )}
                  <div>
                    <h2 className='text-base font-semibold text-gray-900'>
                      {title}
                    </h2>
                    {description && (
                      <p className='text-xs text-gray-600'>
                        {description}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className='p-2 hover:bg-gray-100 rounded-lg transition-colors'
                  aria-label='Close drawer'
                >
                  <X className='h-4 w-4 text-gray-400' />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className='p-6 bg-gray-50/30'>{children}</div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

export default Drawer
